import Vue from 'vue'
import VueRouter from 'vue-router'
import { AuthGuard, isPublicRoute, OrganizationManager } from '../auth'

Vue.use(VueRouter)

let routes = [
	{
		// will match everything
		path: '*',
		component: () => import('../views/404.vue'),
	},
	{
		path: '/',
		name: 'Landing',
		component: () => import('../views/Landing.vue'),
	},
	{
		path: '/dashboard',
		name: 'Dashboard',
		layout: "dashboard",
		// route level code-splitting
		// this generates a separate chunk (about.[hash].js) for this route
		// which is lazy-loaded when the route is visited.
		component: () => import(/* webpackChunkName: "dashboard" */ '../views/Dashboard.vue'),
	},
	{
		path: '/layout',
		name: 'Layout',
		layout: "dashboard",
		component: () => import('../views/Layout.vue'),
	},
	{
		path: '/transactions',
		name: 'Transactions',
		layout: "dashboard",
		component: () => import('../views/Transactions.vue'),
	},
	{
		path: '/reports',
		name: 'Reports',
		layout: "dashboard",
		component: () => import('../views/Reports.vue'),
	},
	{
		path: '/categories',
		name: 'Categories',
		layout: "dashboard",
		component: () => import('../views/Categories.vue'),
	},
	{
		path: '/users',
		name: 'Users',
		layout: "dashboard",
		component: () => import('../views/Users.vue'),
	},
	{
		path: '/Profile',
		name: 'Profile',
		layout: "dashboard",
		component: () => import('../views/Profile.vue'),
	},
	{
		path: '/sign-in',
		name: 'Sign-In',
		component: () => import('../views/Sign-In.vue'),
	},
	{
		path: '/sign-up',
		name: 'Sign-Up',
		component: () => import('../views/Sign-Up.vue'),
	},
	{
		path: '/forgot-password',
		name: 'ForgotPassword',
		component: () => import('../views/ForgotPassword.vue'),
	},
	{
		path: '/reset-password',
		name: 'ResetPassword',
		component: () => import('../views/ResetPassword.vue'),
	},
	{
		path: '/privacy',
		name: 'Privacy',
		component: () => import('../views/Privacy.vue'),
	},
	{
		path: '/terms',
		name: 'Terms',
		component: () => import('../views/Terms.vue'),
	},
	{
		path: '/accept-invitation',
		name: 'AcceptInvitation',
		component: () => import('../views/AcceptInvitation.vue'),
	},
	{
		path: '/create-organization',
		name: 'CreateOrganization',
		layout: "dashboard",
		component: () => import('../views/CreateOrganization.vue'),
	},
]

// Adding layout property from each route to the meta
// object so it can be accessed later.
function addLayoutToRoute( route, parentLayout = "default" )
{
	route.meta = route.meta || {} ;
	route.meta.layout = route.layout || parentLayout ;
	
	if( route.children )
	{
		route.children = route.children.map( ( childRoute ) => addLayoutToRoute( childRoute, route.meta.layout ) ) ;
	}
	return route ;
}

routes = routes.map( ( route ) => addLayoutToRoute( route ) ) ;

const router = new VueRouter({
	mode: 'hash',
	base: process.env.BASE_URL,
	routes,
	scrollBehavior (to, from, savedPosition) {
		if ( to.hash ) {
			return {
				selector: to.hash,
				behavior: 'smooth',
			}
		}
		return {
			x: 0,
			y: 0,
			behavior: 'smooth',
		}
	}
})

// Global navigation guard for authentication
router.beforeEach(async (to, from, next) => {
	// Check if route requires authentication
	if (!isPublicRoute(to.path)) {
		// Protected route - check authentication
		if (AuthGuard.isAuthenticated()) {
			// Routes that don't require organization selection
			const noOrgRequiredRoutes = ['/create-organization', '/profile'];
			if (noOrgRequiredRoutes.includes(to.path.toLowerCase())) {
				next();
				return;
			}

			// User is authenticated - ensure organization is selected
			try {
				const selectedOrgId = OrganizationManager.getSelectedOrganizationId();

				if (!selectedOrgId) {
					// No organization selected - try to auto-select first organization
					console.log('No organization selected - attempting auto-selection');
					let orgs = OrganizationManager.getUserOrganizations();

					if (!orgs || orgs.length === 0) {
						// No organizations in cache - fetch from API
						orgs = await OrganizationManager.fetchUserOrganizations();
					}

					// Filter to only enabled organizations
					const enabledOrgs = orgs ? orgs.filter(org => org.enabled !== false) : [];

					if (enabledOrgs.length > 0) {
						// Try to select an organization
						let selectedOrg = false;
						for (const org of enabledOrgs) {
							try {
								await OrganizationManager.selectOrganization(org.id);
								console.log('Auto-selected organization:', org.id);
								selectedOrg = true;
								break;
							} catch (orgError) {
								console.log(`Could not select organization ${org.id}:`, orgError.message);
								// Continue to try next organization
							}
						}

						if (!selectedOrg) {
							// Could not select any organization - clear selected org and redirect
							console.log('Could not select any organization - redirecting to create organization');
							localStorage.removeItem('selectedOrganizationId');
							next('/create-organization');
							return;
						}
					} else {
						console.log('User has no enabled organizations - redirecting to create organization');
						// User has no enabled organizations - clear selected org and redirect
						localStorage.removeItem('selectedOrganizationId');
						next('/create-organization');
						return;
					}
				}

				next(); // Proceed to route
			} catch (error) {
				console.error('Error ensuring organization selection:', error);
				// If organization selection fails, clear selected org and redirect to create organization
				localStorage.removeItem('selectedOrganizationId');
				next('/create-organization');
			}
		} else {
			console.log('No valid token found - redirecting to sign in');
			next('/sign-in'); // Redirect to sign in
		}
	} else {
		// Public route - allow access
		// If user is authenticated and trying to access landing or sign-in/sign-up, redirect to dashboard
		if ((to.path === '/' || to.path === '/sign-in' || to.path === '/sign-up') && AuthGuard.isAuthenticated()) {
			next('/dashboard');
		} else {
			next();
		}
	}
});

export default router
